import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { apiClient } from "../../../api/httpClient";
import { Box, Typography, CircularProgress, TextField, Button } from "@mui/material";
import Select from "react-select";

function getRetrievalOptionBackground(state) {
  if (state.isSelected) return "#3b82f6";
  if (state.isFocused) return "#eff6ff";
  return "#fff";
}

function getHistoricOptionBackground(state) {
  if (state.isSelected) return "#6200ea";
  if (state.isFocused) return "#f0f0f0";
  return "#fff";
}

function getBondNumberStatusText(option, selectedBondNumber) {
  if (option === selectedBondNumber) return "(currently assigned)";
  const usage = option.is_used ? "used" : "un-used";
  return `(${usage})`;
}

const RETRIEVAL_SELECT_STYLES = {
  control: (base, state) => ({
    ...base,
    height: 60,
    borderRadius: 12,
    borderColor: state.isFocused ? "#3b82f6" : "#d1d5db",
    borderWidth: 2,
    boxShadow: state.isFocused
      ? "0 0 0 3px rgba(59, 130, 246, 0.1), 0 4px 6px -1px rgba(0, 0, 0, 0.1)"
      : "0 1px 3px 0 rgba(0, 0, 0, 0.1)",
    "&:hover": {
      borderColor: "#3b82f6",
      boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
    },
    backgroundColor: "#ffffff",
    transition: "all 0.2s ease-in-out",
    fontSize: "16px",
    fontWeight: "normal",
  }),
  menu: (base) => ({
    ...base,
    zIndex: 9999,
    borderRadius: 12,
    border: "2px solid #e5e7eb",
    boxShadow:
      "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
    overflow: "hidden",
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 9999,
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: getRetrievalOptionBackground(state),
    color: state.isSelected ? "#fff" : "#374151",
    padding: "12px 16px",
    fontSize: "15px",
    fontWeight: "normal",
    "&:hover": {
      backgroundColor: state.isSelected ? "#3b82f6" : "#eff6ff",
    },
    transition: "all 0.15s ease-in-out",
  }),
  placeholder: (base) => ({
    ...base,
    color: "#9ca3af",
    fontSize: "16px",
    fontWeight: "normal",
  }),
  singleValue: (base) => ({
    ...base,
    color: "#374151",
    fontSize: "16px",
    fontWeight: "normal",
  }),
};

const HISTORIC_SELECT_STYLES = {
  control: (base, state) => ({
    ...base,
    height: 56,
    borderRadius: 4,
    borderColor: state.isFocused ? "#6200ea" : "#e0e0e0",
    boxShadow: state.isFocused ? "0 0 0 2px rgba(98, 0, 234, 0.2)" : "none",
    "&:hover": {
      borderColor: "#6200ea",
    },
    backgroundColor: "#ffffff",
    opacity: 1,
  }),
  menu: (base) => ({
    ...base,
    zIndex: 9999,
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 9999,
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: getHistoricOptionBackground(state),
    color: state.isSelected ? "#fff" : "#000",
    "&:hover": {
      backgroundColor: "#f0f0f0",
    },
  }),
};

export const BOND_NUMBER_SELECT_SURFACE_PRESETS = {
  retrieval: {
    headingVariant: "h5",
    headingClassName: "font-bold mb-4",
    headingText: "Bond Number",
    selectStyles: RETRIEVAL_SELECT_STYLES,
    buildSelectProps: (selectedBondNumber) => ({
      formatOptionLabel: (option) => {
        const statusText = getBondNumberStatusText(option, selectedBondNumber);

        return (
          <span>
            <span style={{ fontWeight: "bold" }}>{option.label}</span>
            <span style={{ fontWeight: "normal", marginLeft: "8px" }}>{statusText}</span>
          </span>
        );
      },
      getOptionValue: (option) => option.value,
    }),
  },
  historic: {
    headingVariant: "h6",
    headingClassName: "mb-2",
    headingText: "Select a Bond Number",
    selectStyles: HISTORIC_SELECT_STYLES,
    buildSelectProps: (selectedBondNumber) => ({
      getOptionLabel: (option) => {
        if (option === selectedBondNumber) {
          return `${option.label} ✅`;
        }
        return `${option.label} ${option.is_used ? "❌" : "🟢"}`;
      },
      renderOption: (props, option) => (
        <li {...props}>
          {option.label} {option.is_used ? "❌" : "🟢"}
        </li>
      ),
      getOptionValue: (option) => option.value,
    }),
  },
};

export const BondNumberSelectBase = ({ surface, data, updateData }) => {
  const preset = BOND_NUMBER_SELECT_SURFACE_PRESETS[surface];
  const [bondNumberOptions, setBondNumberOptions] = useState([]);
  const [selectedBondNumber, setSelectedBondNumber] = useState(null);
  const [loading, setLoading] = useState(false);
  const [newBondNumber, setNewBondNumber] = useState("");
  const [creating, setCreating] = useState(false);
  const [hasSetDefault, setHasSetDefault] = useState(false);

  useEffect(() => {
    const fetchBondNumbers = async () => {
      setLoading(true);
      try {
        const response = await apiClient("/bondnumbers/", "GET");
        const formattedOptions = response.map((bond) => ({
          label: `${bond.bond_number}`,
          value: bond.bond_number,
          ...bond,
        }));

        setBondNumberOptions(formattedOptions.reverse());
      } catch (error) {
        console.error("Error fetching bond numbers:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchBondNumbers();
  }, []);

  useEffect(() => {
    if (!hasSetDefault && bondNumberOptions.length > 0) {
      if (data?.bond_number?.bond_number) {
        const preFilledBondNumber = bondNumberOptions.find(
          (option) => option.value === data.bond_number.bond_number
        );

        if (preFilledBondNumber) {
          setSelectedBondNumber(preFilledBondNumber);
          updateData({ ...data, bond_number: preFilledBondNumber });
        }
      } else if (bondNumberOptions.length > 0) {
        const availableBondNumber = bondNumberOptions.find((option) => !option.is_used);
        if (availableBondNumber) {
          setSelectedBondNumber(availableBondNumber);
          updateData({ ...data, bond_number: availableBondNumber });
        }
      }
      setHasSetDefault(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bondNumberOptions, data]);

  const handleChange = (selectedOption) => {
    setSelectedBondNumber(selectedOption);
    updateData({ ...data, bond_number: selectedOption });
  };

  const handleCreateBondNumber = async () => {
    if (!newBondNumber) {
      alert("Please enter a bond number.");
      return;
    }

    try {
      setCreating(true);
      const response = await apiClient("/single-bondnumber/", "POST", {
        bond_number: newBondNumber,
      });

      const newOption = {
        label: `${response.bond_number}`,
        value: response.bond_number,
        ...response,
      };

      setBondNumberOptions((prevOptions) => [...prevOptions, newOption]);
      setSelectedBondNumber(newOption);
      updateData({ ...data, bond_number: newOption });
      setNewBondNumber("");
    } catch (error) {
      console.error("Error creating bond number:", error);
      alert(`Failed to create bond number: ${error.response?.data?.error || "Unknown error"}`);
    } finally {
      setCreating(false);
    }
  };

  const surfaceSelectProps = preset.buildSelectProps(selectedBondNumber);

  return (
    <Box
      className="bg-gray-100 p-6 rounded-lg shadow-md max-w-3xl mx-auto mt-8"
      sx={{ border: "1px solid #e0e0e0" }}
    >
      <Typography variant={preset.headingVariant} className={preset.headingClassName}>
        {preset.headingText}
      </Typography>

      {loading ? (
        <Box display="flex" justifyContent="center" mt={2}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Select
            options={bondNumberOptions}
            value={selectedBondNumber}
            onChange={handleChange}
            isClearable
            isSearchable
            placeholder="Select or create a bond number"
            styles={preset.selectStyles}
            {...surfaceSelectProps}
          />

          <Typography variant="h6" className="mt-6 font-normal">
            Or create a new bond number
          </Typography>

          <Box display="flex" gap={2} mt={2}>
            <TextField
              label="Enter Bond Number"
              value={newBondNumber}
              onChange={(e) => setNewBondNumber(e.target.value)}
              fullWidth
            />
            <Button
              variant="contained"
              color="primary"
              onClick={handleCreateBondNumber}
              disabled={creating}
            >
              {creating ? "Creating..." : "Create"}
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
};

BondNumberSelectBase.propTypes = {
  surface: PropTypes.oneOf(["retrieval", "historic"]).isRequired,
  data: PropTypes.object,
  updateData: PropTypes.func.isRequired,
};
